import React from 'react';
import { message } from 'antd';
import { CreditCard } from '@phosphor-icons/react';
import type { CombinedFinanceLine, CombinedFinancingSummary } from './financingCombinedSummary';
import './oneButton-css/FinancingCombinedView.css';

export type FinancingCombinedViewProps = {
    summary: CombinedFinancingSummary;
    loading?: boolean;
    /** Opens Generate Quote modal with fields populated from recommended combined picks. */
    onOpenGenerateQuote?: () => void;
    onOpenSelectedGenerateQuote?: (event:any) => void;
    /** Permission / context block only — not product design misses. */
    generateQuoteDisabled?: boolean;
    generateQuoteDisabledReason?: string;
    getQuoteDesignBlockedReason?: () => string | undefined;
    getQuoteDesignBlockedReasonForLine?: (line: CombinedFinanceLine) => string | undefined;
    onQuoteDesignBlocked?: (reason: string) => void;
    getSummaryQuoteSyncBlockedHint?: () => string | undefined;
    getLineQuoteSyncBlockedHint?: (line: CombinedFinanceLine) => string | undefined;
};

export type FinancingDealHealthChipProps = {
    monthlyDisplay: string;
    className?: string;
};

/** Pill shown in Financing Options header (combined monthly total). */
export function FinancingDealHealthChip({ monthlyDisplay, className }: FinancingDealHealthChipProps) {
    const showComingSoon = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        e.preventDefault();
        message.info('Deal Health — coming soon.');
    };

    return (
        <button
            type="button"
            className={`obd-financing-combined__deal-health${className ? ` ${className}` : ''}`}
            aria-label={`Deal health ${monthlyDisplay}. Coming soon.`}
            title="Deal Health (coming soon)"
            onClick={showComingSoon}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') showComingSoon(e);
            }}
        >
            <span className="obd-financing-combined__deal-health-label">Deal Health</span>
            <span className="obd-financing-combined__deal-health-value">{monthlyDisplay}</span>
        </button>
    );
}

function partnerAccentClass(partner: string): string {
    const k = partner.toLowerCase().replace(/\s+/g, '');
    if (k.includes('lightreach')) return 'lightreach';
    if (k.includes('goodleap')) return 'goodleap';
    if (k === 'cash') return 'cash';
    if (k.startsWith('igs')) return 'igs';
    if (k.includes('enfin')) return 'enfin';
    return 'default';
}

export const FinancingCombinedView: React.FC<FinancingCombinedViewProps> = ({
    summary,
    loading = false,
    onOpenGenerateQuote,
    onOpenSelectedGenerateQuote,
    generateQuoteDisabled = false,
    generateQuoteDisabledReason,
    getQuoteDesignBlockedReason,
    getQuoteDesignBlockedReasonForLine,
    onQuoteDesignBlocked,
    getSummaryQuoteSyncBlockedHint,
    getLineQuoteSyncBlockedHint,
}) => {
    const totalDisplay =
        summary.totalMonthly > 0 ? `$${summary.totalMonthly.toFixed(2)}/mo` : loading ? '…' : '—';

    const summarySyncHint = getSummaryQuoteSyncBlockedHint?.();
    const summaryDesignBlockedReason = getQuoteDesignBlockedReason?.();
    const canAttemptQuote =
        !generateQuoteDisabled && !loading && summary.lines.length > 0 && !!onOpenGenerateQuote;
    const summaryVisuallyBlocked =
        generateQuoteDisabled || !!summaryDesignBlockedReason || !!summarySyncHint;
    const quoteBlockedTitle = generateQuoteDisabledReason;

    const handleSummaryActivate = () => {
        if (!canAttemptQuote) return;
        if (summaryDesignBlockedReason) {
            onQuoteDesignBlocked?.(summaryDesignBlockedReason);
            return;
        }
        onOpenGenerateQuote?.();
    };

    const handleSummarySelected = (line: CombinedFinanceLine) => {
        if (!canAttemptQuote) return;
        const lineDesignBlockedReason = getQuoteDesignBlockedReasonForLine?.(line);
        if (lineDesignBlockedReason) {
            onQuoteDesignBlocked?.(lineDesignBlockedReason);
            return;
        }
        const product = line.productLabel === 'Solar system' ? 'Solar' : line.productLabel;
        const partner = line.partner;
        onOpenSelectedGenerateQuote?.({ product, partner });
    };

    return (
        <div
            className={`obd-financing-combined${canAttemptQuote ? ' obd-financing-combined--clickable' : ''}${summaryVisuallyBlocked ? ' obd-financing-combined--quote-disabled' : ''}`}
            aria-label="Combined financing summary"
        >
            <div
                className="obd-financing-combined__head"
                onClick={handleSummaryActivate}
                role={canAttemptQuote ? 'button' : undefined}
                tabIndex={canAttemptQuote ? 0 : undefined}
                title={
                    canAttemptQuote
                        ? summaryDesignBlockedReason ??
                          summarySyncHint ??
                          'Click to generate quote.'
                        : quoteBlockedTitle
                }
                onKeyDown={(e) => {
                    if (!canAttemptQuote) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSummaryActivate();
                    }
                }}>
                <div className="obd-financing-combined__head-main">
                    <span className="obd-financing-combined__head-icon" aria-hidden>
                        <CreditCard size={20} weight="duotone" />
                    </span>
                    <div>
                        <p className="obd-financing-combined__label">Combined Financing</p>
                        <p className="obd-financing-combined__total">{totalDisplay}</p>
                        <p className="obd-financing-combined__breakdown">{summary.breakdown}</p>
                    </div>
                </div>
            </div>

            <div className="obd-financing-combined__lines">
                {summary.lines.length === 0 ? (
                    <p className="obd-financing-combined__empty">
                        {loading
                            ? 'Loading pricing…'
                            : 'No recommended pricing yet. Open Best pricing options or refresh pricing.'}
                    </p>
                ) : (
                    summary.lines.map((line) => {
                        const lineSyncHint = getLineQuoteSyncBlockedHint?.(line);
                        const lineDesignBlockedReason = getQuoteDesignBlockedReasonForLine?.(line);
                        const lineVisuallyBlocked = !!lineDesignBlockedReason || !!lineSyncHint;
                        return (
                        <article key={line.id} className="obd-financing-combined__line">
                            <div
                                className={`obd-financing-combined__line-head${lineVisuallyBlocked ? ' obd-financing-combined__line-head--quote-disabled' : ''}`}
                                onClick={() => handleSummarySelected(line)}
                                role={canAttemptQuote ? 'button' : undefined}
                                tabIndex={canAttemptQuote ? 0 : undefined}
                                title={
                                    canAttemptQuote
                                        ? lineDesignBlockedReason ??
                                          lineSyncHint ??
                                          'Click to generate quote.'
                                        : quoteBlockedTitle
                                }
                                onKeyDown={(e) => {
                                    if (!canAttemptQuote) return;
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSummarySelected(line);
                                    }
                                }}>
                                <span
                                    className={`obd-financing-combined__line-dot obd-financing-combined__line-dot--${partnerAccentClass(line.partner)}`}
                                    aria-hidden
                                />
                                <div className="obd-financing-combined__line-copy">
                                    <p className="obd-financing-combined__line-title">
                                        {line.partnerLabel}
                                        {line.method ? ` · ${line.method}` : ''}
                                    </p>
                                    <p className="obd-financing-combined__line-monthly">
                                        ${line.monthly.toFixed(2)}/mo
                                    </p>
                                    <p className="obd-financing-combined__line-detail">
                                        {line.productLabel}
                                        {line.detail ? ` · ${line.detail}` : ''}
                                    </p>
                                </div>
                            </div>
                        </article>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default FinancingCombinedView;
